import { useCallback, useState } from 'react';

import type { DocumentSource } from './documentSource';
import { DocumentViewerDialog } from './DocumentViewerDialog';

interface OpenedDocument {
  source: DocumentSource;
  title: string;
  contentType?: string;
}

/**
 * One viewer for a screen that lists many documents.
 *
 * A list of twenty rows should not mount twenty dialogs. The screen holds one,
 * and `open` swaps which document is in it — so the cost of the viewer is paid
 * once per screen rather than once per row, and the pdf.js worker only ever
 * exists for the document actually being read.
 *
 * Returns the element rather than expecting the caller to render the dialog
 * itself, because the pairing is the point: a caller that took only `open`
 * would silently do nothing if they forgot the dialog, and that is a bug that
 * looks exactly like a dead button.
 */
export const useDocumentViewer = () => {
  const [opened, setOpened] = useState<OpenedDocument | null>(null);

  const open = useCallback((document: OpenedDocument) => {
    setOpened(document);
  }, []);

  const close = useCallback(() => {
    setOpened(null);
  }, []);

  // Unmounted on close rather than left open={false}: the viewer holds a parsed
  // PDF and a worker, and keeping the last-read document alive behind a closed
  // dialog is what would turn a twenty-row queue into twenty leaked workers.
  const viewer =
    opened === null ? null : (
      <DocumentViewerDialog
        open
        onClose={close}
        source={opened.source}
        title={opened.title}
        contentType={opened.contentType}
      />
    );

  return { open, close, viewer };
};
